'use strict';

import debounce from './debounce';
import i18n from '../i18n/i18n';

/**
 * Navigation Module
 * Handles both mobile and desktop navigation with focus management
 * @version 3.1.0 - Using centralized i18n module (ES5 compatible)
 */
const navs = {
  // State
  mobileMainNavToggled: false,
  desktopNavBreakpoint: window.matchMedia('(min-width: 992px)'),
  focusTrapHandler: null,
  lastBreakpointMatch: null,

  // Debug and init guard
  debug: false,
  _initialized: false,
  _handlers: null,
  _lastActivationWasPointer: false, // track how the menu was activated

  // DOM cache
  dom: {},

  /**
   * Get current language labels from i18n
   * @returns {Object} Language-specific labels
   */
  getLabels() {
    // Fallbacks if i18n not ready
    const defaults = {
      navigation: 'Navigation',
      closeMainMenu: 'Close main menu',
      openMainMenu: 'Open main menu',
      closeSubMenu: 'Close submenu',
      openSubMenu: 'Open submenu',
      subMenuOpen: 'submenu opened',
      subMenuClosed: 'submenu closed',
      mainMenuOpen: 'Main menu opened',
      mainMenuClosed: 'Main menu closed',
      mainMenuClosedFocusReturned: 'Main menu closed, focus returned',
    };
    const ns = i18n && typeof i18n.getNamespace === 'function' ? i18n.getNamespace('nav') : null;
    return Object.assign({}, defaults, ns || {});
  },

  /**
   * Cache all DOM elements
   * @returns {void}
   */
  cacheDom() {
    this.dom = {
      body: document.body,

      // Mobile navigation
      mobileMainNavWrap: document.getElementById('mobileMainNavWrap'),
      mobileMainNav: document.querySelector('.main-nav--mobile'),
      mobileMainNavToggles: Array.from(document.querySelectorAll('[data-mobile-main-nav-toggle]')),
      mobileSubNavToggles: Array.from(document.querySelectorAll('.main-nav--mobile button[aria-expanded]')),

      // Desktop navigation
      desktopMainNav: document.querySelector('.js-main-nav-desktop'),
      desktopMainNavItems: [],
      desktopNavToggles: [],

      // Live region for announcements
      liveRegion: null,
    };

    // Desktop nav items (only if desktop nav exists)
    if (this.dom.desktopMainNav) {
      this.dom.desktopMainNavItems = Array.from(
        this.dom.desktopMainNav.querySelectorAll('.main-nav__item')
      );
      this.dom.desktopNavToggles = Array.from(
        this.dom.desktopMainNav.querySelectorAll('.main-nav--desktop .main-nav__link[aria-expanded]')
      );
    }
  },

  /**
   * Create ARIA live region for announcements
   * @returns {void}
   */
  createLiveRegion() {
    if (!this.dom.liveRegion) {
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('class', 'sr-only');
      liveRegion.setAttribute('lang', i18n.getLanguage());
      liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
      document.body.appendChild(liveRegion);
      this.dom.liveRegion = liveRegion;
    }
  },

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   * @returns {void}
   */
  announce(message) {
    if (this.dom.liveRegion) {
      // Clear first to ensure announcement is read even if same message
      this.dom.liveRegion.textContent = '';
      setTimeout(() => {
        this.dom.liveRegion.textContent = message;
      }, 100);
    }
  },

  /**
   * Setup accessibility attributes for navigation elements
   * @returns {void}
   */
  setupAccessibilityAttributes() {
    const labels = this.getLabels();

    // Ensure navigation has proper role and label
    if (this.dom.mobileMainNav && !this.dom.mobileMainNav.hasAttribute('aria-label')) {
      this.dom.mobileMainNav.setAttribute('aria-label', labels.navigation);
    }

    if (this.dom.desktopMainNav && !this.dom.desktopMainNav.hasAttribute('aria-label')) {
      this.dom.desktopMainNav.setAttribute('aria-label', labels.navigation);
    }

    // Setup mobile main nav toggles
    this.dom.mobileMainNavToggles.forEach((toggle) => {
      if (!toggle.hasAttribute('aria-label')) {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        const label = isExpanded ? labels.closeMainMenu : labels.openMainMenu;
        toggle.setAttribute('aria-label', label);
      }

      // Set aria-controls if mobileMainNavWrap has ID
      if (this.dom.mobileMainNavWrap && this.dom.mobileMainNavWrap.id) {
        toggle.setAttribute('aria-controls', this.dom.mobileMainNavWrap.id);
      } else if (this.dom.mobileMainNavWrap && !this.dom.mobileMainNavWrap.id) {
        this.dom.mobileMainNavWrap.id = 'mobileMainNavWrap';
        toggle.setAttribute('aria-controls', 'mobileMainNavWrap');
      }
    });

    // Setup desktop nav toggles
    this.dom.desktopNavToggles.forEach((toggle) => {
      const subNav = toggle.parentElement.querySelector('.main-nav__sub-nav');
      if (subNav) {
        // Generate ID for submenu if needed
        if (!subNav.id) {
          subNav.id = `submenu-${Math.random().toString(36).substr(2, 9)}`;
        }
        toggle.setAttribute('aria-controls', subNav.id);

        // Add aria-label if not present
        if (!toggle.hasAttribute('aria-label')) {
          const linkTextEl = toggle.querySelector('.main-nav__link-text');
          const linkText = linkTextEl ? linkTextEl.textContent.trim() : '';
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          const action = isExpanded ? labels.closeSubMenu : labels.openSubMenu;
          toggle.setAttribute('aria-label', `${linkText} - ${action}`);
        }
      }
    });

    // Setup mobile sub-nav toggles
    this.dom.mobileSubNavToggles.forEach((toggle) => {
      const linkEl = toggle.closest('.main-nav__link');
      const linkTextEl = linkEl ? linkEl.querySelector('.main-nav__link-text') : null;
      const linkText = linkTextEl ? linkTextEl.textContent.trim() : '';

      if (!toggle.hasAttribute('aria-label') && linkText) {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        const action = isExpanded ? labels.closeSubMenu : labels.openSubMenu;
        toggle.setAttribute('aria-label', `${linkText} - ${action}`);
      }

      // Set aria-controls
      const navItem = toggle.closest('.main-nav__item');
      const subNav = navItem ? navItem.querySelector('.main-nav__sub-nav') : null;
      if (subNav) {
        if (!subNav.id) {
          subNav.id = `mobile-submenu-${Math.random().toString(36).substr(2, 9)}`;
        }
        toggle.setAttribute('aria-controls', subNav.id);
      }
    });
  },

  /**
   * Update toggle button label based on state
   * @param {HTMLElement} toggle - Toggle button element
   * @param {boolean} isExpanded - Whether menu is expanded
   * @param {boolean} isMainMenu - Whether it's main menu (vs submenu)
   * @returns {void}
   */
  updateToggleLabel(toggle, isExpanded, isMainMenu = true) {
    const labels = this.getLabels();

    if (isMainMenu) {
      const label = isExpanded ? labels.closeMainMenu : labels.openMainMenu;
      toggle.setAttribute('aria-label', label);
    } else {
      const linkEl = toggle.closest('.main-nav__link, .main-nav__item');
      const linkTextEl = linkEl ? linkEl.querySelector('.main-nav__link-text') : null;
      const linkText = linkTextEl ? linkTextEl.textContent.trim() : '';
      const action = isExpanded ? labels.closeSubMenu : labels.openSubMenu;
      toggle.setAttribute('aria-label', linkText ? `${linkText} - ${action}` : action);
    }
  },

  /**
   * Close sidemenu tabs if open
   * @returns {void}
   */
  closeSidemenuIfOpen() {
    // Check if sidemenu component exists and has open tabs
    const openTab = document.querySelector('.article-tab.active:not(.d-none)');
    if (openTab) {
      this.log('Closing sidemenu because main nav is opening');
      const togglers = Array.from(document.querySelectorAll('.mobile-action-togglers button'));
      const tabs = Array.from(document.querySelectorAll('.article-tab'));

      tabs.forEach(function(tab) {
        tab.classList.remove('active');
        tab.classList.add('d-none');
        tab.setAttribute('aria-hidden', 'true');
      });

      togglers.forEach(function(toggler) {
        toggler.classList.remove('active');
        toggler.setAttribute('aria-expanded', 'false');
      });
    }
  },

  /**
   * Toggle mobile main navigation
   * @param {boolean} [forceState] - Force open (true) or closed (false), toggles if undefined
   * @returns {void}
   */
  toggleMobileMainNav(forceState) {
    if (!this.dom.mobileMainNavWrap || !this.dom.mobileMainNav) {
      this.log('toggleMobileMainNav aborted: missing mobile nav DOM');
      return;
    }

    const shouldOpen = typeof forceState === 'boolean' ? forceState : !this.mobileMainNavToggled;
    this.log('toggleMobileMainNav', { forceState, shouldOpen });

    this.mobileMainNavToggled = shouldOpen;

    // Update DOM
    this.dom.mobileMainNavWrap.style.display = shouldOpen ? 'flex' : 'none';
    this.dom.body.classList.toggle('mobile-main-nav-visible', shouldOpen);

    const labels = this.getLabels();

    // Update all toggle buttons
    this.dom.mobileMainNavToggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      toggle.classList.toggle('selected', shouldOpen);
      this.updateToggleLabel(toggle, shouldOpen, true);
    });

    if (shouldOpen) {
      // Close sidemenu before opening main nav
      this.closeSidemenuIfOpen();
      this.onMobileNavOpen();
      this.announce(labels.mainMenuOpen);
    } else {
      this.onMobileNavClose();
      this.announce(labels.mainMenuClosed);
    }
  },

  /**
   * Handle mobile nav opening - focuses first element and sets up focus trap
   * @returns {void}
   */
  onMobileNavOpen() {
    this.log('onMobileNavOpen');
    // On pointer activation, skip "skip to content" and focus first nav link/button within .main-nav__link
    const targetFocusable = this._lastActivationWasPointer
      ? this.dom.mobileMainNav.querySelector('.main-nav__link a, .main-nav__link button')
      : this.dom.mobileMainNav.querySelector('a, button');

    if (targetFocusable) {
      setTimeout(() => {
        targetFocusable.focus();
      }, 50);
    }

    // Setup focus trap
    this.setupMobileFocusTrap();
  },

  /**
   * Handle mobile nav closing - removes focus trap
   * @returns {void}
   */
  onMobileNavClose() {
    this.log('onMobileNavClose');
    this.removeMobileFocusTrap();
  },

  /**
   * Get all focusable elements in mobile nav (only inside the nav, not header)
   * @returns {Array<HTMLElement>} Array of focusable elements
   */
  getMobileFocusableElements() {
    const els = Array.from(
      this.dom.mobileMainNavWrap.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')
    ).filter(function(el) {
      return el.offsetParent !== null;
    });
    this.log('getMobileFocusableElements', els.length);
    return els;
  },

  /**
   * Check if element is within mobile nav focus trap area
   * @param {HTMLElement} element - Element to check
   * @returns {boolean} True if element is within mobile nav wrap
   */
  isInMobileNavTrap(element) {
    if (!element) return false;

    // Check if it's inside mobile nav wrap (not header)
    return this.dom.mobileMainNavWrap && this.dom.mobileMainNavWrap.contains(element);
  },

  /**
   * Setup focus trap for mobile navigation
   * Prevents focus from leaving the mobile nav when open
   * @returns {void}
   */
  setupMobileFocusTrap() {
    const self = this;

    this.removeMobileFocusTrap();

    this.focusTrapHandler = function(e) {
      if (!self.mobileMainNavToggled) return;
      if (e.key === 'Tab') {
        const focusables = self.getMobileFocusableElements();

        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        // Check if focus is within trap
        const isWithin = self.isInMobileNavTrap(active);

        // If focus escaped, bring it back
        if (!isWithin) {
          e.preventDefault();
          first.focus();
          return;
        }

        // Handle wrapping
        if (e.shiftKey) {
          if (active === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
    this.log('focusTrap attached');
  },

  /**
   * Remove focus trap event listener
   * @returns {void}
   */
  removeMobileFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
      this.log('focusTrap removed');
    }
  },

  /**
   * Initialize mobile sub-navigation toggles
   * Sets up click handlers for expandable sub-menus
   * @returns {void}
   */
  initMobileSubNavToggles() {
    const self = this;
    const labels = this.getLabels();

    this.dom.mobileSubNavToggles.forEach(function(toggle) {
      // prevent double-binding
      if (toggle.dataset.initMobileSub === '1') return;

      toggle.addEventListener('click', function(evt) {
        evt.stopPropagation();
        evt.preventDefault();

        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        const newState = !isExpanded;

        this.setAttribute('aria-expanded', String(newState));
        self.updateToggleLabel(this, newState, false);

        // Toggle class on the button itself
        const linkElement = this.closest('.main-nav__link');
        if (linkElement) {
          linkElement.classList.toggle('main-nav__link--sub-nav-toggled', newState);
        }

        // Announce state change
        const announcement = newState ? labels.subMenuOpen : labels.subMenuClosed;
        self.announce(announcement);
      });

      toggle.dataset.initMobileSub = '1';
    });

    // Auto-open current page's sub-nav
    if (this.dom.mobileMainNavWrap) {
      const currentToggle = this.dom.mobileMainNavWrap.querySelector(
        '.main-nav__link--selected button[aria-expanded]'
      );
      if (currentToggle && currentToggle.getAttribute('aria-expanded') === 'false') {
        currentToggle.click();
      }
    }
  },

  /**
   * Initialize desktop navigation
   * Sets up click handlers for desktop mega menu toggles
   * @returns {void}
   */
  initDesktopMainNav() {
    if (!this.dom.desktopMainNav) {
      this.log('initDesktopMainNav skipped: no desktop nav');
      return;
    }

    const self = this;
    const itemOpenClass = 'main-nav__item--open';

    this.dom.desktopNavToggles.forEach(function(toggle) {
      // prevent double-binding
      if (toggle.dataset.initDesktopToggle === '1') return;

      toggle.addEventListener('click', function(evt) {
        // Check if has submenu
        if (this.parentElement.querySelector('.main-nav__sub-nav')) {
          evt.preventDefault();
          evt.stopPropagation();

          const isOpen = this.parentElement.classList.contains(itemOpenClass);

          if (isOpen) {
            self.closeDesktopMainNav();
          } else {
            self.openDesktopMainNav(evt.currentTarget);
          }
        }
      });

      toggle.dataset.initDesktopToggle = '1';
    });

    this.log('initDesktopMainNav bound', this.dom.desktopNavToggles.length);
  },

  /**
   * Open desktop main nav item (mega menu)
   * @param {HTMLElement} target - The toggle element to open
   * @returns {void}
   */
  openDesktopMainNav(target) {
    const itemOpenClass = 'main-nav__item--open';
    const labels = this.getLabels();

    // Close all first
    this.closeDesktopMainNav();

    // Open specific item
    if (target) {
      const item = target.closest('.main-nav__item');
      if (item && item.querySelector('.main-nav__sub-nav')) {
        item.classList.add(itemOpenClass);
        target.setAttribute('aria-expanded', 'true');
        this.updateToggleLabel(target, true, false);

        // Announce
        const linkTextEl = target.querySelector('.main-nav__link-text');
        const linkText = linkTextEl ? linkTextEl.textContent.trim() : '';
        this.announce(`${linkText} ${labels.subMenuOpen}`);
      }
    }
  },

  /**
   * Close all desktop nav items (mega menus)
   * @param {boolean} skipAnnouncement - Whether to skip screen reader announcement
   * @returns {void}
   */
  closeDesktopMainNav(skipAnnouncement = false) {
    const itemOpenClass = 'main-nav__item--open';
    const self = this;
    let wasOpen = false;

    this.dom.desktopMainNavItems.forEach(function(item) {
      if (item.classList.contains(itemOpenClass)) {
        wasOpen = true;
      }
      item.classList.remove(itemOpenClass);
    });

    this.dom.desktopNavToggles.forEach(function(toggle) {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        toggle.setAttribute('aria-expanded', 'false');
        self.updateToggleLabel(toggle, false, false);
      }
    });

    if (wasOpen && !skipAnnouncement) {
      const labels = this.getLabels();
      this.announce(labels.subMenuClosed);
    }
  },

  /**
   * Handle breakpoint changes (debounced)
   * Closes mobile nav when switching to desktop view
   * @returns {void}
   */
  handleBreakpointChange: debounce(function() {
    const isDesktop = this.desktopNavBreakpoint.matches;
    this.log('handleBreakpointChange', {
      from: this.lastBreakpointMatch,
      to: isDesktop,
      mobileOpen: this.mobileMainNavToggled,
    });

    // Handle breakpoint change
    if (this.lastBreakpointMatch !== isDesktop) {
      this.lastBreakpointMatch = isDesktop;

      // Close mobile nav when switching to desktop
      if (isDesktop && this.mobileMainNavToggled) {
        this.toggleMobileMainNav(false);
      }
    }
  }, 100),

  /**
   * Handle keyboard events
   * Closes navigation on Escape key press
   * @param {KeyboardEvent} e - Keyboard event
   * @returns {void}
   */
  handleKeydown(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;

    const labels = this.getLabels();

    this.log('handleKeydown Escape', {
      mobileOpen: this.mobileMainNavToggled,
      isDesktop: this.desktopNavBreakpoint.matches,
    });

    // Close mobile nav
    if (this.mobileMainNavToggled) {
      this.toggleMobileMainNav(false);

      if (this.dom.mobileMainNavToggles[0]) {
        this.dom.mobileMainNavToggles[0].focus();
        this.announce(labels.mainMenuClosedFocusReturned);
      }
      return;
    }

    // Close desktop nav
    if (this.desktopNavBreakpoint.matches && this.dom.desktopMainNav) {
      const activeToggle = this.dom.desktopMainNav.querySelector(
        '.main-nav__link[aria-expanded="true"]'
      );

      if (activeToggle && this.dom.desktopMainNav.contains(document.activeElement)) {
        this.closeDesktopMainNav();
        activeToggle.focus();
      }
    }
  },

  /**
   * Handle clicks outside desktop nav
   * Closes desktop navigation when clicking outside
   * @param {MouseEvent} e - Mouse event
   * @returns {void}
   */
  handleOutsideClick(e) {
    if (this.desktopNavBreakpoint.matches && this.dom.desktopMainNav) {
      if (!this.dom.desktopMainNav.contains(e.target)) {
        this.log('outside click -> close desktop nav');
        this.closeDesktopMainNav(true); // Skip announcement for outside clicks
      }
    }
  },

  /**
   * Initialize navigation module
   * Sets up all event listeners and initial state
   * @returns {void}
   */
  init() {
    const self = this;

    if (this._initialized) {
      this.log('init called again; skipping');
      return;
    }

    // Cache DOM elements
    this.cacheDom();

    const hasMobileNav = !!(this.dom.mobileMainNavWrap && this.dom.mobileMainNav);
    if (!hasMobileNav) {
      this.log('Mobile navigation elements not found');
    }

    // Create live region
    this.createLiveRegion();

    // Setup accessibility attributes
    this.setupAccessibilityAttributes();

    // Stable global handlers so we can avoid duplicates
    this._handlers = {
      keydown: function(e) { self.handleKeydown(e); },
      clickOutside: function(e) { self.handleOutsideClick(e); },
      resize: function() { self.handleBreakpointChange(); },
    };

    this.log('Binding event listeners');

    if (hasMobileNav) {
      this.dom.mobileMainNavToggles.forEach(function(toggle) {
        if (toggle.dataset.initMobileMain === '1') return;
        toggle.addEventListener('click', function(evt) {
          evt.stopPropagation();
          evt.preventDefault();
          // detail > 0 indicates pointer-initiated click; detail === 0 indicates keyboard activation
          self._lastActivationWasPointer = evt && typeof evt.detail === 'number' ? evt.detail > 0 : true;
          self.toggleMobileMainNav();
        });
        toggle.dataset.initMobileMain = '1';
      });

      // Mobile sub-nav toggles
      self.initMobileSubNavToggles();
    }

    // Desktop nav
    this.initDesktopMainNav();

    // Global listeners (single bind)
    document.addEventListener('keydown', this._handlers.keydown);
    document.addEventListener('click', this._handlers.clickOutside);
    window.addEventListener('resize', this._handlers.resize);

    // Initial breakpoint state
    this.lastBreakpointMatch = this.desktopNavBreakpoint.matches;

    this._initialized = true;
    this.log('Initialized', { lang: i18n && i18n.getLanguage ? i18n.getLanguage() : 'unknown' });
  },

  // Add optional destroy to unbind global listeners if needed
  destroy() {
    // Remove focus trap
    this.removeMobileFocusTrap();

    if (this._handlers) {
      document.removeEventListener('keydown', this._handlers.keydown);
      document.removeEventListener('click', this._handlers.clickOutside);
      window.removeEventListener('resize', this._handlers.resize);
    }

    this._initialized = false;
    this.log('Destroyed');
  },

  log() {
    try {
      const globalDebug =
        (typeof window !== 'undefined' && (window.__SP_DEBUG || localStorage.getItem('sp-debug') === '1')) || false;
      if (this.debug || globalDebug) {
        const ts = new Date().toISOString();
        const args = Array.prototype.slice.call(arguments);
        console.log.apply(console, ['[Navs]', ts].concat(args));
      }
    } catch (_) {
      // ignore logging errors
    }
  }
};

module.exports = navs;
